import React, { useEffect, useMemo, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X, MagnifyingGlass } from "@phosphor-icons/react";

const STATUSES = ["in_stock", "in_service", "in_transit", "under_maintenance", "retired"];

export default function AssetTrackingPage() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [form, setForm] = useState({
    asset_tag: "", serial_number: "", product_id: "",
    current_location_id: "", site_id: "", project_code: "",
    status: "in_stock", acquired_date: "", cost: "", notes: "",
  });

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/alm/assets"), api.get("/alm/products"),
      api.get("/alm/locations"), api.get("/alm/sites"),
    ]);
    setRows(a.data); setProducts(b.data); setLocations(c.data); setSites(d.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!q) return true;
      const t = q.toLowerCase();
      return [r.asset_tag, r.serial_number, r.product_code, r.product_name, r.current_location_name, r.site_number, r.project_code]
        .some(v => (v || "").toLowerCase().includes(t));
    });
  }, [rows, q, statusFilter]);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/alm/assets", {
      ...form,
      current_location_id: form.current_location_id || null,
      site_id: form.site_id || null,
      cost: parseFloat(form.cost || 0),
    });
    setOpen(false);
    setForm({ ...form, asset_tag: "", serial_number: "", product_id: "", current_location_id: "", site_id: "", project_code: "", acquired_date: "", cost: "", notes: "" });
    load();
  };

  const remove = async (id) => { if (window.confirm("Delete this asset?")) { await api.delete(`/alm/assets/${id}`); load(); } };

  const statusBadge = (s) => {
    const map = {
      in_service: "bg-green-100 text-green-800",
      in_stock: "bg-blue-100 text-blue-800",
      in_transit: "bg-yellow-100 text-yellow-800",
      under_maintenance: "bg-orange-100 text-orange-800",
      retired: "bg-gray-200 text-gray-700",
    };
    return <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-1 ${map[s] || "bg-gray-100"}`}>{(s || "").replace("_", " ")}</span>;
  };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Asset Tracking"
        subtitle="Search, filter and track every asset by tag, serial, location and site."
        testid="alm-assets-title"
        actions={
          <button onClick={() => setOpen(true)} data-testid="alm-asset-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
            <Plus size={14} weight="bold" /> New Asset
          </button>
        }
      />
      <div className="p-8 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <MagnifyingGlass size={14} weight="bold" className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search tag, serial, product, location…" data-testid="alm-asset-search" className="w-full border border-gray-300 pl-9 pr-3 py-2.5 text-sm" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="alm-asset-statusfilter" className="border border-gray-300 px-3 py-2.5 text-sm">
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
          <div className="text-xs text-gray-500" data-testid="alm-asset-count">{filtered.length} of {rows.length}</div>
        </div>
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-assets-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Asset Tag", "Serial", "Product", "Location", "Site", "Project", "Status", "Cost", "Acquired", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.asset_tag}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.serial_number || "—"}</td>
                  <td className="px-4 py-3"><span className="font-mono text-xs font-bold">{r.product_code}</span> <span className="text-xs text-gray-600">— {r.product_name}</span></td>
                  <td className="px-4 py-3 text-xs">{r.current_location_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.site_number || "—"}</td>
                  <td className="px-4 py-3 text-xs">{r.project_code || "—"}</td>
                  <td className="px-4 py-3">{statusBadge(r.status)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmt(r.cost)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.acquired_date)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`alm-asset-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-500">No assets matching filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-asset-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Create</div>
                <h2 className="text-2xl font-black tracking-tight">New Asset</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                ["asset_tag", "Asset Tag *", "text", true],
                ["serial_number", "Serial Number", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-asset-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
                </div>
              ))}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Product *</label>
                <select required value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })} data-testid="alm-asset-product" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— Select Product —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Current Location</label>
                <select value={form.current_location_id} onChange={e => setForm({ ...form, current_location_id: e.target.value })} data-testid="alm-asset-loc" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— None —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Site</label>
                <select value={form.site_id} onChange={e => setForm({ ...form, site_id: e.target.value })} data-testid="alm-asset-site" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— None —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.site_number} — {s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="alm-asset-status" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </div>
              {[
                ["project_code", "Project Code", "text"],
                ["acquired_date", "Acquired Date", "date"],
                ["cost", "Cost", "number"],
                ["notes", "Notes", "text"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-asset-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-asset-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Create Asset</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
